import React, { useState, useEffect } from "react";
import axios from "axios";
import { Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { CITIES_SELECT, SERVICES_SELECT } from "../mock";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  "Free consultation with a care manager",
  "Verified, trained & background-checked caregivers",
  "Hassle-free replacements, transparent pricing",
];

const BookingForm = ({ defaultService = "" }) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    service: defaultService,
    notes: "",
  });

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name.";

    const digits = form.phone.replace(/\D/g, "").replace(/^(91|0)/, "");
    if (!digits) errs.phone = "Please enter your phone number.";
    else if (!/^[6-9]\d{9}$/.test(digits))
      errs.phone = "Enter a valid 10-digit mobile number.";

    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
      errs.email = "Please enter a valid email address.";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Keep the service field in sync when navigating between service pages
  useEffect(() => {
    setForm((f) => ({ ...f, service: defaultService }));
  }, [defaultService]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast({
        title: "Please check the form",
        description: "Some details need fixing before we can submit.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const digits = form.phone.replace(/\D/g, "").replace(/^(91|0)/, "");
      await axios.post(`${API}/bookings`, { ...form, phone: digits });
      trackEvent("contact_form_submit", { form_name: "contact" });
      toast({
        title: "Request received ✓",
        description: `Thanks ${form.name}, a care manager will call you within 10 minutes.`,
      });
      setForm({ name: "", phone: "", email: "", city: "", service: defaultService, notes: "" });
      setErrors({});
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Please try again, or call us at +91 96117 61212.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-[#F1F4F8] border border-[#D5DFE8] rounded-xl px-4 py-3 text-[14px] text-[#1B2A4A] placeholder:text-[#aeb7c2] outline-none focus:border-[#159A90] focus:ring-2 focus:ring-[#159A90]/20 transition";

  return (
    <section id="book" className="py-20 md:py-28 px-5 md:px-8">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
        {/* Left copy */}
        <div className="fade-up">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-px w-6 bg-[#159A90]" />
            <span className="text-[12px] font-bold tracking-[0.16em] text-[#159A90] uppercase">
              Book a Caregiver
            </span>
          </div>
          <h2 className="font-heading font-extrabold text-[#1B2A4A] text-[32px] md:text-[42px] leading-tight">
            Share your care need. We'll do the rest.
          </h2>
          <p className="text-[#57616e] mt-4 text-[16px] leading-relaxed">
            Tell us a little about your loved one and their condition. A dedicated
            care manager will reach out within 10 minutes to match you with the
            right caregiver.
          </p>
          <ul className="space-y-3 mt-7">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#E7EDF3] shrink-0">
                  <Check size={14} className="text-[#159A90]" />
                </span>
                <span className="text-[15px] text-[#3a4757]">{p}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-[28px] p-7 md:p-9 border border-[#DEE6EE] shadow-[0_24px_60px_rgba(0,0,0,0.1)] fade-up">
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">Your name</label>
                <input
                  className={`${inputCls} ${errors.name ? "border-red-400 focus:border-red-400 focus:ring-red-200" : ""}`}
                  placeholder="e.g. Ananya Sharma"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                />
                {errors.name && <p className="text-[12px] text-red-500 mt-1.5">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">Phone</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  className={`${inputCls} ${errors.phone ? "border-red-400 focus:border-red-400 focus:ring-red-200" : ""}`}
                  placeholder="10-digit mobile number"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                />
                {errors.phone && <p className="text-[12px] text-red-500 mt-1.5">{errors.phone}</p>}
              </div>
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">Email</label>
              <input
                type="email"
                className={`${inputCls} ${errors.email ? "border-red-400 focus:border-red-400 focus:ring-red-200" : ""}`}
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
              />
              {errors.email && <p className="text-[12px] text-red-500 mt-1.5">{errors.email}</p>}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">City</label>
                <Select value={form.city} onValueChange={(v) => update("city", v)}>
                  <SelectTrigger className="w-full bg-[#F1F4F8] border-[#D5DFE8] rounded-xl px-4 py-6 text-[14px] focus:ring-2 focus:ring-[#159A90]/20">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {CITIES_SELECT.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">Service needed</label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger className="w-full bg-[#F1F4F8] border-[#D5DFE8] rounded-xl px-4 py-6 text-[14px] focus:ring-2 focus:ring-[#159A90]/20">
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES_SELECT.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-[#3a4757] mb-1.5">Notes (optional)</label>
              <textarea
                className={`${inputCls} resize-none h-24`}
                placeholder="Tell us about the condition, routine or any special needs..."
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-6 py-3.5 rounded-full bg-[#159A90] text-white font-semibold hover:bg-[#0E7A72] transition-colors shadow-[0_10px_30px_rgba(21,154,144,0.35)] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Book Now"}
            </button>
            <p className="text-[12px] text-[#8b96a3] text-center leading-relaxed">
              By submitting, you agree to be contacted by Healthsqur over phone,
              email or WhatsApp.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
